import {FormControl} from 'platform/components';
import {v4 as uuidV4} from 'uuid';
import {SnapshotFrom} from 'xstate';

import {ReactNode, useCallback, useEffect, useMemo, useRef} from 'react';
import {UseFormReturn, useWatch} from 'react-hook-form';

import {AuditCategoryOfStructure, AuditDataAssetsV2Body} from '@dms/api/core';
import {useLazyGetMakeModelWithMakeQuery} from '@dms/api/sales';
import {VehicleTypeEnumObject} from '@dms/api/shared';
import {VehicleTypeEnum} from '@dms/api/vehicle';
import i18n, {FALLBACK_LANGUAGE} from '@dms/i18n';
import {useVehicleCatalogue, useMandatoryFields} from '@dms/shared';

import {TestIdProps} from 'shared/web';

import {useVehicleFlow} from '../core/hooks/useVehicleFlow';
import {machine} from '../core/machine/machine';
import {BaseVehicle} from '../core/types/BaseVehicle';
import {createFieldRegistry} from './FieldRegistry';
import {VehicleFormContext, VehicleFormContextValue} from './VehicleFormContext';

export interface VehicleFormProviderProps<T extends BaseVehicle = BaseVehicle> extends TestIdProps {
  children: ((context: VehicleFormContextValue<T>) => ReactNode) | ReactNode;
  formApi: UseFormReturn<T>;
  control: FormControl<T>;
  showMandatoryFields?: boolean;
}

export function VehicleFormProvider<T extends BaseVehicle = BaseVehicle>(
  props: VehicleFormProviderProps<T>
) {
  const {
    children,
    'data-testid': dataTestId,
    ...contextValue
  } = props as VehicleFormProviderProps<BaseVehicle>;
  const baseTestId = useMemo(() => dataTestId || 'vehicle-form', [dataTestId]);
  const isInitialized = useRef(false);
  const fieldRegistryRef = useRef(createFieldRegistry());

  // Generate a unique session ID to distinguish between different form sessions
  const sessionId = useRef<string | null>(uuidV4());

  const {getMandatoryFields} = useMandatoryFields();

  const {initMachine, stopMachine, state, send, machineActor} = useVehicleFlow();

  // Lazy RTK Query trigger for fetching makes/models by vehicle type (uses cache when available)
  const [fetchMakeModelWithMake] = useLazyGetMakeModelWithMakeQuery();

  /**
   * Stable async callback injected into the machine's typeWatcherActor.
   * Called before TYPE_FIELD_CHANGED is sent to check if the current make
   * exists in the new vehicle type's catalog.
   * Uses RTK Query cache — resolves immediately if already cached.
   */
  const getCatalogMakesForType = useCallback(
    async (vehicleType: string): Promise<string[]> => {
      const result = await fetchMakeModelWithMake(
        {
          vehicleType,
          lang: [i18n.resolvedLanguage ?? FALLBACK_LANGUAGE],
        },
        /* preferCacheValue */ true
      );
      return result.data?.map((entry) => entry.make) ?? [];
    },
    [fetchMakeModelWithMake]
  );

  /**
   * Stable async callback injected into the machine's typeWatcherActor.
   * Called before TYPE_FIELD_CHANGED is sent (only when make is preserved) to check if
   * the current model exists in the new type+make catalog.
   * Uses RTK Query cache — resolves immediately if already cached.
   * Returns flat model keys (formModelFamily format, e.g. MAKE_BMW-MODELFAMILY_330) for the given type+make.
   */
  const getCatalogModelsForTypeAndMake = useCallback(
    async (vehicleType: string, make: string): Promise<string[]> => {
      const result = await fetchMakeModelWithMake(
        {
          vehicleType,
          make,
          lang: [i18n.resolvedLanguage ?? FALLBACK_LANGUAGE],
        },
        /* preferCacheValue */ true
      );
      // Flatten all model keys (model.model) — these match the formModelFamily field format
      const models: string[] = [];
      for (const entry of result.data ?? []) {
        for (const model of entry.models ?? []) {
          if (model.model) {
            models.push(model.model);
          }
        }
      }
      return models;
    },
    [fetchMakeModelWithMake]
  );

  const vehicleTypeWatch =
    useWatch({
      control: contextValue.control,
      name: 'type',
    }) ?? [];

  const fuelTypeWatch = useWatch<BaseVehicle, 'fuelType'>({
    control: contextValue.control,
    name: 'fuelType',
  });

  const hybridTypeWatch = useWatch<BaseVehicle, 'hybridType'>({
    control: contextValue.control,
    name: 'hybridType',
  });

  const selectedVehicleType = Array.isArray(vehicleTypeWatch)
    ? vehicleTypeWatch.at(0)
    : vehicleTypeWatch;
  const vehicleType = selectedVehicleType as VehicleTypeEnumObject | null;
  const [catalogueApi] = useVehicleCatalogue(vehicleType, false);

  const currentFuelType = fuelTypeWatch?.at(0);
  const currentHybridType = hybridTypeWatch?.at(0);

  const mandatoryFields = useMemo(
    () =>
      getMandatoryFields({
        vehicleType: vehicleType as VehicleTypeEnum | undefined,
        fuelType: currentFuelType,
        hybridType: currentHybridType,
      }),
    [getMandatoryFields, vehicleType, currentFuelType, currentHybridType]
  );

  /**
   * Audit form reference
   * Used to store the audit form instance
   *
   * @type {React.MutableRefObject<FormApi<TConditionForm> | null>}
   */
  const auditForm = useRef<unknown | null>(null);

  /**
   * Audit assets reference
   * Used to store the audit assets
   */
  const auditAssets = useRef<Record<string, AuditDataAssetsV2Body>>({});

  /**
   * Audit structure reference
   * Used to store the audit structure
   */
  const auditStructure = useRef<AuditCategoryOfStructure[]>([]);

  /**
   * Memoize catalogueApi to prevent constant re-renders since useVehicleCatalogue
   * creates new function references on every call.
   * Only update when vehicleType changes, because that's the only time the catalogueApi needs to be re-created.
   */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedCatalogueApi = useMemo(() => catalogueApi, [vehicleType]);

  useEffect(() => {
    // Defer the machine initialization until after the form has mounted
    const timerId = setTimeout(() => {
      initMachine(contextValue.formApi, getCatalogMakesForType, getCatalogModelsForTypeAndMake);
      isInitialized.current = true;
    }, 0);

    return () => {
      clearTimeout(timerId);
      stopMachine();
      // reset form values to prevent stale data on next form creation
      auditForm.current = null;
      auditAssets.current = {};
      auditStructure.current = [];
      // fieldRegistryRef.current?.clear(); // Removed: breaks StrictMode double-mounting and reference counting
      contextValue.formApi.reset();
    };
    // Mount-only effect: getCatalogMakesForType and getCatalogModelsForTypeAndMake are intentionally
    // omitted from the dependency array. Both are useCallback-wrapped with fetchMakeModelWithMake as
    // their only dep; RTK Query guarantees fetchMakeModelWithMake is a stable reference for the
    // lifetime of the component, so neither callback will ever change identity after mount.
    // Re-initialising the machine on every render would reset in-flight state unnecessarily.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Extract vehicle variant information from machine state
   */
  const vehicleVariant = state.context.vehicleVariant;

  /**
   * Extract vehicle ID from machine state
   */
  const vehicleId = state.context.vehicleId ?? null;

  /**
   * Create a stable state snapshot that only updates when relevant values change
   * This prevents unnecessary re-renders when other parts of state change
   * We spread the full state but only update when specific context values change
   */
  const stableStateSnapshot: SnapshotFrom<typeof machine> = useMemo(
    () => ({
      ...state,
      context: {
        ...state.context,
        vehicleVariant: state.context.vehicleVariant,
        vehicleId: state.context.vehicleId,
        errors: state.context.errors,
        decodedVinData: state.context.decodedVinData,
        vinDecodingError: state.context.vinDecodingError,
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      state.value,
      state.context.vehicleVariant,
      state.context.vehicleId,
      state.context.errors,
      state.context.decodedVinData,
      state.context.vinDecodingError,
    ]
  );

  /**
   * Audit context
   * Used to store the audit form instance
   *
   */
  const auditContext = useMemo(
    () => ({
      setForm: (form: unknown) => {
        auditForm.current = form;
      },
      getForm: () => auditForm.current,
      getAssets: () => auditAssets.current,
      setAssets: (assets: Record<string, AuditDataAssetsV2Body>) => {
        auditAssets.current = assets;
      },
      setStructure: (structure: AuditCategoryOfStructure[]) => {
        auditStructure.current = structure;
      },
      getStructure: () => auditStructure.current ?? [],
    }),
    [] // No dependencies needed - functions only use refs
  );

  /**
   * Memoize the context value to prevent unnecessary re-renders of consumers.
   */
  const value: VehicleFormContextValue<BaseVehicle> = useMemo(
    () => ({
      control: contextValue.control,
      formApi: contextValue.formApi,
      catalogueApi: memoizedCatalogueApi,
      watch: contextValue.formApi.watch,
      globalState: stableStateSnapshot, // Use stable snapshot instead of full state
      sendMachineEvent: send,
      mandatoryFields: contextValue.showMandatoryFields ? mandatoryFields : [],
      vehicleType,
      vehicleVariant,
      vehicleId,
      baseTestId,
      auditContext,
      sessionId: sessionId.current,
      machineActor,
      fieldRegistry: fieldRegistryRef.current,
    }),
    [
      contextValue.control,
      contextValue.formApi,
      contextValue.showMandatoryFields,
      memoizedCatalogueApi,
      stableStateSnapshot, // Use stable snapshot instead of full state
      send,
      vehicleType,
      vehicleVariant,
      vehicleId,
      baseTestId,
      mandatoryFields,
      auditContext,
      machineActor,
    ]
  );

  return (
    <VehicleFormContext.Provider value={value}>
      {typeof children === 'function' ? children(value) : children}
    </VehicleFormContext.Provider>
  );
}
